/**
 * Minimal framer-motion replacement built on CSS transitions.
 *
 * Supports the subset the app uses: `motion.<tag>` / `motion(Component)` with
 * `initial` / `animate` / `exit` / `transition`, `AnimatePresence` (keeps
 * exiting children mounted until their exit transition ends), `LayoutGroup`
 * (no-op) and `useAnimation()` controls with awaitable `start`.
 *
 * Layout animations (`layout` prop) are accepted and ignored.
 */
import React from "react";

type Num = number | string;
export interface TargetAndTransition {
  opacity?: number;
  x?: Num;
  y?: Num;
  scale?: number;
  scaleX?: number;
  scaleY?: number;
  rotate?: number;
  height?: Num;
  width?: Num;
  marginTop?: Num;
  marginBottom?: Num;
  flex?: string;
  backgroundColor?: string;
  color?: string;
  transition?: Transition;
  [key: string]: unknown;
}
export type Target = TargetAndTransition;

export interface Transition {
  duration?: number;
  delay?: number;
  ease?: string | readonly number[];
  type?: string;
  layout?: Transition;
  [key: string]: unknown;
}

export interface MotionProps {
  initial?: TargetAndTransition | false | string;
  animate?: TargetAndTransition | AnimationControls | string;
  exit?: TargetAndTransition | string;
  transition?: Transition;
  variants?: Record<string, TargetAndTransition>;
  layout?: boolean | "position" | "size";
  layoutId?: string;
  whileHover?: TargetAndTransition;
  whileTap?: TargetAndTransition;
  onAnimationComplete?: () => void;
}

const EASE: Record<string, string> = {
  linear: "linear",
  easeIn: "ease-in",
  easeOut: "ease-out",
  easeInOut: "ease-in-out",
  circIn: "cubic-bezier(0.55, 0, 1, 0.45)",
  circOut: "cubic-bezier(0, 0.55, 0.45, 1)",
  backOut: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  anticipate: "cubic-bezier(0.36, 0, 0.66, -0.56)"
};

function easeToCss(ease: Transition["ease"]): string {
  if (Array.isArray(ease)) return `cubic-bezier(${ease.join(",")})`;
  if (!ease) return "ease-out";
  return EASE[ease as string] ?? (ease as string);
}

const TRANSFORM_KEYS = ["x", "y", "scale", "scaleX", "scaleY", "rotate"] as const;
const px = (v: Num | undefined) => (typeof v === "number" ? `${v}px` : v);

function targetToStyle(t: TargetAndTransition | undefined, resolve: (t: TargetAndTransition | string) => TargetAndTransition | undefined): React.CSSProperties {
  if (!t) return {};
  const target = resolve(t) ?? {};
  const { transition: _t, ...rest } = target;
  const style: Record<string, unknown> = {};
  const transforms: string[] = [];
  for (const [k, v] of Object.entries(rest)) {
    if (v === undefined) continue;
    if ((TRANSFORM_KEYS as readonly string[]).includes(k)) {
      if (k === "x") transforms.push(`translateX(${px(v as Num)})`);
      else if (k === "y") transforms.push(`translateY(${px(v as Num)})`);
      else if (k === "rotate") transforms.push(`rotate(${v}deg)`);
      else transforms.push(`${k}(${v})`);
    } else {
      style[k] = v;
    }
  }
  if (transforms.length) style.transform = transforms.join(" ");
  return style as React.CSSProperties;
}

function durationOf(...ts: Array<Transition | undefined>): number {
  for (const t of ts) if (t && typeof t.duration === "number") return t.duration;
  return 0.3;
}

function transitionCss(t: Transition | undefined, fallback?: Transition): string {
  const src = t ?? fallback ?? {};
  const d = durationOf(t, fallback);
  const delay = src.delay ? ` ${src.delay}s` : "";
  return `all ${d}s ${easeToCss(src.ease)}${delay}`;
}

/* ------------------------------------------------------------------------ */
/* Animation controls                                                       */
/* ------------------------------------------------------------------------ */

type Listener = (target: TargetAndTransition) => Promise<void>;

export interface AnimationControls {
  start: (target: TargetAndTransition) => Promise<void>;
  stop: () => void;
  set: (target: TargetAndTransition) => void;
  /** @internal */
  subscribe: (l: Listener) => () => void;
  /** @internal */
  __isControls: true;
}

export function useAnimation(): AnimationControls {
  return React.useMemo<AnimationControls>(() => {
    const listeners = new Set<Listener>();
    return {
      __isControls: true,
      subscribe: (l) => {
        listeners.add(l);
        return () => listeners.delete(l);
      },
      start: (target) => Promise.all(Array.from(listeners).map((l) => l(target))).then(() => undefined),
      stop: () => undefined,
      set: (target) => {
        listeners.forEach((l) => void l({ ...target, transition: { duration: 0 } }));
      }
    };
  }, []);
}

const isControls = (v: unknown): v is AnimationControls =>
  Boolean(v && typeof v === "object" && (v as AnimationControls).__isControls);

/* ------------------------------------------------------------------------ */
/* AnimatePresence                                                          */
/* ------------------------------------------------------------------------ */

interface PresenceContextValue {
  isPresent: boolean;
  onExitComplete?: () => void;
  initial: boolean;
}
const PresenceContext = React.createContext<PresenceContextValue | null>(null);

export function AnimatePresence({
  children,
  initial = true,
  mode: _mode,
  onExitComplete
}: {
  children?: React.ReactNode;
  initial?: boolean;
  mode?: "sync" | "wait" | "popLayout";
  onExitComplete?: () => void;
}) {
  const valid = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement[];
  const keyOf = (el: React.ReactElement, i: number) => (el.key ?? `__idx_${i}`) as React.Key;
  const [rendered, setRendered] = React.useState<Array<{ key: React.Key; el: React.ReactElement; present: boolean }>>(
    () => valid.map((el, i) => ({ key: keyOf(el, i), el, present: true }))
  );
  const mounted = React.useRef(false);
  React.useEffect(() => {
    mounted.current = true;
  }, []);

  // Reconcile current children with what we are rendering; mark removed ones as exiting.
  const currentKeys = valid.map(keyOf);
  const signature = currentKeys.join("|");
  React.useEffect(() => {
    setRendered((prev) => {
      const next: typeof prev = [];
      const seen = new Set<React.Key>();
      for (const item of prev) {
        const idx = currentKeys.indexOf(item.key);
        if (idx >= 0) {
          next.push({ key: item.key, el: valid[idx], present: true });
          seen.add(item.key);
        } else {
          next.push({ ...item, present: false });
        }
      }
      valid.forEach((el, i) => {
        const k = keyOf(el, i);
        if (!seen.has(k) && !next.some((n) => n.key === k)) next.push({ key: k, el, present: true });
      });
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Fast path: while keys are unchanged, render the latest children directly so
  // prop updates are not delayed by a render.
  const output = rendered.map((item) => {
    const idx = currentKeys.indexOf(item.key);
    const el = idx >= 0 ? valid[idx] : item.el;
    const present = idx >= 0;
    return (
      <PresenceContext.Provider
        key={item.key}
        value={{
          isPresent: present,
          initial: initial || mounted.current,
          onExitComplete: () => {
            setRendered((prev) => prev.filter((p) => p.key !== item.key));
            onExitComplete?.();
          }
        }}
      >
        {el}
      </PresenceContext.Provider>
    );
  });
  return <>{output}</>;
}

/* ------------------------------------------------------------------------ */
/* motion()                                                                 */
/* ------------------------------------------------------------------------ */

type MotionComponentProps<P> = Omit<P, keyof MotionProps> & MotionProps & { style?: React.CSSProperties; children?: React.ReactNode };

function createMotion<P extends { style?: React.CSSProperties }>(Component: React.ElementType) {
  const Motion = React.forwardRef<Element, MotionComponentProps<P>>(function Motion(props, ref) {
    const {
      initial,
      animate,
      exit,
      transition,
      variants,
      layout: _layout,
      layoutId: _layoutId,
      whileHover,
      whileTap,
      onAnimationComplete,
      style,
      ...rest
    } = props as MotionComponentProps<P> & Record<string, unknown>;
    const presence = React.useContext(PresenceContext);
    const isPresent = presence?.isPresent ?? true;
    const resolve = React.useCallback(
      (t: TargetAndTransition | string): TargetAndTransition | undefined =>
        typeof t === "string" ? variants?.[t] : t,
      [variants]
    );

    const controls = isControls(animate) ? animate : null;
    const animateTarget = isControls(animate) ? undefined : (animate as TargetAndTransition | string | undefined);

    // Whether to start from `initial` on mount.
    const skipInitial = initial === false || (presence ? !presence.initial : false);
    const [current, setCurrent] = React.useState<TargetAndTransition | string | undefined>(() =>
      skipInitial ? animateTarget : (initial as TargetAndTransition | string | undefined) ?? animateTarget
    );
    const [activeTransition, setActiveTransition] = React.useState<Transition | undefined>(transition);
    const [hover, setHover] = React.useState(false);
    const [tap, setTap] = React.useState(false);
    const completeRef = React.useRef<(() => void) | null>(null);
    const exiting = !isPresent;

    // Mount -> animate.
    React.useEffect(() => {
      if (controls) return;
      const id = requestAnimationFrame(() => {
        setCurrent(animateTarget);
        setActiveTransition(transition);
      });
      return () => cancelAnimationFrame(id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(animateTarget), controls]);

    // Exit.
    React.useEffect(() => {
      if (!exiting) return;
      if (!exit) {
        presence?.onExitComplete?.();
        return;
      }
      setCurrent(exit);
      setActiveTransition(transition);
      const d = durationOf(resolve(exit)?.transition, transition) + (transition?.delay ?? 0);
      const t = setTimeout(() => presence?.onExitComplete?.(), d * 1000 + 20);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exiting]);

    // Imperative controls.
    React.useEffect(() => {
      if (!controls) return;
      return controls.subscribe((target) => {
        setCurrent(target);
        setActiveTransition(target.transition ?? transition);
        const d = durationOf(target.transition, transition) + ((target.transition?.delay as number) ?? 0);
        return new Promise<void>((res) => setTimeout(res, d * 1000));
      });
    }, [controls, transition]);

    React.useEffect(() => {
      if (!onAnimationComplete) return;
      const d = durationOf(activeTransition, transition);
      const t = setTimeout(() => onAnimationComplete(), d * 1000);
      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(current)]);

    completeRef.current = null;

    const resolvedCurrent = typeof current === "string" ? variants?.[current] : current;
    const computed: React.CSSProperties = {
      ...style,
      ...targetToStyle(resolvedCurrent, resolve),
      ...(hover && whileHover ? targetToStyle(whileHover, resolve) : {}),
      ...(tap && whileTap ? targetToStyle(whileTap, resolve) : {}),
      transition: transitionCss(resolvedCurrent?.transition, activeTransition ?? transition)
    };
    // `height: auto` cannot be transitioned; fall back to no transition for it.
    const handlers = {
      ...(whileHover
        ? {
            onMouseEnter: (e: React.MouseEvent) => {
              setHover(true);
              (rest.onMouseEnter as ((e: React.MouseEvent) => void) | undefined)?.(e);
            },
            onMouseLeave: (e: React.MouseEvent) => {
              setHover(false);
              (rest.onMouseLeave as ((e: React.MouseEvent) => void) | undefined)?.(e);
            }
          }
        : {}),
      ...(whileTap
        ? {
            onMouseDown: (e: React.MouseEvent) => {
              setTap(true);
              (rest.onMouseDown as ((e: React.MouseEvent) => void) | undefined)?.(e);
            },
            onMouseUp: (e: React.MouseEvent) => {
              setTap(false);
              (rest.onMouseUp as ((e: React.MouseEvent) => void) | undefined)?.(e);
            }
          }
        : {})
    };
    return <Component ref={ref} style={computed} {...rest} {...handlers} />;
  });
  Motion.displayName = `motion(${typeof Component === "string" ? Component : (Component as { displayName?: string }).displayName ?? "Component"})`;
  return Motion;
}

type MotionFactory = (<P extends { style?: React.CSSProperties }>(
  Component: React.ComponentType<P>
) => React.ForwardRefExoticComponent<MotionComponentProps<P> & React.RefAttributes<Element>>) & {
  [K in keyof React.JSX.IntrinsicElements]: React.ForwardRefExoticComponent<
    MotionComponentProps<React.JSX.IntrinsicElements[K]> & React.RefAttributes<Element>
  >;
};

const cache = new Map<string, unknown>();
export const motion = new Proxy(createMotion as unknown as MotionFactory, {
  apply: (_target, _this, args: [React.ComponentType]) => createMotion(args[0]),
  get: (_target, prop: string) => {
    if (!cache.has(prop)) cache.set(prop, createMotion(prop as unknown as React.ElementType));
    return cache.get(prop);
  }
}) as MotionFactory;

export function LayoutGroup({ children }: { children?: React.ReactNode; id?: string }) {
  return <>{children}</>;
}

export function useReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}
